import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { ui, statusStyle } from "@/lib/ui";
import { Loader2, Rocket, Pencil, Plus, ExternalLink, Eye, RefreshCw, Trash2, Globe, Search, EyeOff, Upload } from "lucide-react";
import { PreviewShell, EditPanel } from "@/components/national/NationalPreview";

const fmt = (d) => (d ? new Date(d).toLocaleString() : "—");
const badge = (s) => <span className={`${ui.badge} ${statusStyle[s] || statusStyle.draft}`}>{s}</span>;
const busySub = (s) => ["deploying", "deleting"].includes(s.status) || s.gen_status === "running";

function Info({ label, children, testid }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wider font-mono text-slate-500">{label}</p>
      <div className="text-sm text-slate-200 mt-1 break-all" data-testid={testid}>{children}</div>
    </div>
  );
}

function Confirm({ text, onYes, onNo }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="confirm-dialog">
      <div className={`${ui.card} p-6 max-w-md w-full`}>
        <p className="text-slate-200">{text}</p>
        <div className="flex justify-end gap-2 mt-5">
          <button data-testid="confirm-cancel" onClick={onNo} className={ui.btnGhost}>Cancel</button>
          <button data-testid="confirm-yes" onClick={onYes} className={ui.btnDanger}>Delete</button>
        </div>
      </div>
    </div>
  );
}

function DomainCard({ site, reload }) {
  const [domain, setDomain] = useState(site.custom_domain || site.intended_domain || "");
  const [www, setWww] = useState(true);
  const [busy, setBusy] = useState(false);
  const [statuses, setStatuses] = useState(site.domain_statuses || {});
  const connect = async () => {
    setBusy(true);
    try { await api.post(`/sites/${site.id}/connect-domain`, { domain, include_www: www }); toast.success("Domain connected — DNS and SSL may take a few minutes"); reload(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const check = async () => {
    try { const { data } = await api.get(`/sites/${site.id}/domain-status`); setStatuses(Object.fromEntries(Object.entries(data.domains).map(([k, v]) => [k, v.status]))); }
    catch (e) { toast.error(apiErr(e)); }
  };
  if (!site.deployed_url) return <div className={`${ui.card} p-5 text-sm text-slate-400`} data-testid="domain-card">Publish the website once to connect <b className="text-slate-200">{site.intended_domain}</b>.</div>;
  return (
    <div className={`${ui.card} p-5 space-y-3`} data-testid="domain-card">
      <h2 className={ui.cardTitle}>Domain</h2>
      {site.custom_domain ? (
        <div className="space-y-1 text-sm">
          {Object.entries(statuses).map(([k, v]) => <p key={k} className="text-slate-300"><Globe className="w-3.5 h-3.5 inline mr-1 text-emerald-400" />{k} — <span className="font-mono">{v || "pending"}</span></p>)}
          <button data-testid="domain-check-btn" onClick={check} className="text-xs text-indigo-300">Re-check status</button>
        </div>
      ) : (
        <>
          <p className="text-xs text-slate-500">The domain must be an Active zone in your Cloudflare account. DNS records are created automatically.</p>
          <div className="flex flex-wrap gap-2 items-center">
            <input data-testid="domain-input" className={`${ui.input} max-w-xs`} value={domain} onChange={(e) => setDomain(e.target.value)} />
            <label className="text-sm text-slate-300 inline-flex items-center gap-1.5"><input type="checkbox" checked={www} onChange={(e) => setWww(e.target.checked)} /> include www</label>
            <button data-testid="connect-domain-btn" onClick={connect} disabled={busy || !domain.trim()} className={ui.btnPrimary}>{busy ? <Loader2 className="w-4 h-4 spin" /> : <Globe className="w-4 h-4" />} Connect</button>
          </div>
        </>
      )}
    </div>
  );
}

function SubRow({ s, act, canManage, navigate, natId }) {
  const busy = busySub(s);
  const status = s.gen_status === "running" ? "generating" : s.status;
  const btn = "p-1.5 rounded border border-slate-700 hover:bg-slate-800 disabled:opacity-40";
  return (
    <tr className="border-b border-slate-800/60 align-top" data-testid={`subpage-row-${s.id}`}>
      <td className="px-4 py-3 text-sm text-slate-100 font-medium">{s.business.name}
        {s.gen_status === "failed" && <p className="text-[11px] text-rose-400 mt-1 max-w-xs">{s.gen_error}</p>}
        {s.last_error && <p className="text-[11px] text-rose-400 mt-1 max-w-xs">{s.last_error}</p>}
        {s.has_changes && s.is_published && <p className="text-[11px] text-amber-300 mt-1">Unpublished changes</p>}
      </td>
      <td className="px-4 py-3 text-xs font-mono text-slate-400">{s.live_url ? <a href={s.live_url} target="_blank" rel="noreferrer" className="text-emerald-400 hover:underline">{s.live_url}</a> : `/${s.slug}/`}</td>
      <td className="px-4 py-3 text-sm text-slate-300">{s.location || "—"}</td>
      <td className="px-4 py-3" data-testid={`subpage-status-${s.id}`}>{busy ? <span className={`${ui.badge} ${statusStyle.running}`}><Loader2 className="w-3 h-3 spin" />{status}</span> : badge(status)}</td>
      <td className="px-4 py-3 text-xs text-slate-500 font-mono">{fmt(s.updated_at)}</td>
      <td className="px-4 py-3">
        <div className="flex flex-nowrap gap-1.5">
          <a data-testid={`sub-preview-${s.id}`} href={s.preview_url || undefined} target="_blank" rel="noreferrer" className={`${btn} ${!s.preview_url ? "pointer-events-none opacity-40" : ""}`} title="Preview"><Eye className="w-4 h-4" /></a>
          {canManage && <>
            <button data-testid={`sub-edit-${s.id}`} disabled={busy} onClick={() => navigate(`/national/${natId}/subpages/${s.id}`)} className={btn} title="Edit"><Pencil className="w-4 h-4" /></button>
            <button data-testid={`sub-regen-${s.id}`} disabled={busy} onClick={() => act(s, "regenerate")} className={btn} title="Regenerate content"><RefreshCw className="w-4 h-4" /></button>
            <button data-testid={`sub-publish-${s.id}`} disabled={busy || !s.has_draft} onClick={() => act(s, "publish")} className={btn} title={s.is_published ? "Republish" : "Publish"}><Upload className="w-4 h-4" /></button>
            <button data-testid={`sub-unpublish-${s.id}`} disabled={busy || !s.is_published} onClick={() => act(s, "unpublish")} className={btn} title="Unpublish"><EyeOff className="w-4 h-4" /></button>
            <button data-testid={`sub-delete-${s.id}`} disabled={busy} onClick={() => act(s, "delete")} className={`${btn} text-rose-300`} title="Delete"><Trash2 className="w-4 h-4" /></button>
          </>}
          {s.live_url && <a data-testid={`sub-open-${s.id}`} href={s.live_url} target="_blank" rel="noreferrer" className={btn} title="Open live page"><ExternalLink className="w-4 h-4" /></a>}
        </div>
      </td>
    </tr>
  );
}

export default function NationalDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [site, setSite] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [frameKey, setFrameKey] = useState(0);
  const lastVid = useRef(null);

  const load = useCallback(() => api.get(`/national/${id}`).then(({ data }) => setSite(data)).catch((e) => toast.error(apiErr(e))), [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (site?.current_version_id && lastVid.current && lastVid.current !== site.current_version_id) setFrameKey((k) => k + 1);
    if (site) lastVid.current = site.current_version_id;
  }, [site]);

  const polling = site && (site.gen_status === "running" || site.status === "deploying" || site.subpages.some(busySub));
  useEffect(() => {
    if (!polling) return undefined;
    const iv = setInterval(load, 4000);
    return () => clearInterval(iv);
  }, [polling, load]);

  const subs = useMemo(() => (site?.subpages || []).filter((s) => {
    const t = q.trim().toLowerCase();
    if (t && ![s.business.name, s.slug, s.location].some((x) => (x || "").toLowerCase().includes(t))) return false;
    if (status && (s.gen_status === "running" ? "generating" : s.status) !== status) return false;
    return true;
  }), [site, q, status]);

  const call = async (fn, msg, reload = true) => { try { await fn(); if (msg) toast.success(msg); if (reload) load(); } catch (e) { toast.error(apiErr(e)); } };
  const act = (s, a) => {
    const base = `/national/${id}/subpages/${s.id}`;
    if (a === "delete") return setConfirm({ text: `Delete ${s.business.name}? ${s.is_published ? "It will be removed from the live website; the national site and other pages stay as they are." : ""}`, run: () => call(() => api.delete(base), "Deleting page…") });
    const msgs = { regenerate: "Regenerating content…", publish: "Publishing — verifying the live URL…", unpublish: "Unpublishing…" };
    return call(() => api.post(`${base}/${a}`), msgs[a]);
  };
  const deleteSite = () => setConfirm({ text: `Delete ${site.name} and all ${site.subpages.length} business subpage(s)? This removes it from hosting.`, run: () => call(async () => { await api.delete(`/sites/${id}`); navigate("/websites"); }, "Website deleted", false) });

  if (!site) return <div className="p-10 text-slate-500"><Loader2 className="w-5 h-5 spin" /></div>;
  const genRunning = site.gen_status === "running";
  const deploying = site.status === "deploying";
  const live = site.status === "live";

  return (
    <div className="animate-fade-up space-y-5" data-testid="national-detail">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link to="/websites" className="text-sm text-slate-400">← My Websites</Link>
          <h1 className={`${ui.pageHeader} mt-2 flex items-center gap-3`} data-testid="national-name">{site.name} <span data-testid="national-status">{badge(genRunning ? "generating" : site.status)}</span></h1>
          <p className={`${ui.muted} mt-1`}>National website · {site.category_name}</p>
        </div>
        {site.can_manage && (
          <div className="flex flex-wrap gap-2">
            <button data-testid="national-edit-btn" onClick={() => navigate(`/national/${id}/edit`)} disabled={genRunning || deploying} className={ui.btnGhost}><Pencil className="w-4 h-4" /> Edit Website</button>
            <button data-testid="national-publish-btn" onClick={() => call(() => api.post(`/national/${id}/publish`), "Publishing — verifying deployment…")} disabled={genRunning || deploying || !site.current_version_id} className={ui.btnPrimary}>
              {deploying ? <Loader2 className="w-4 h-4 spin" /> : <Rocket className="w-4 h-4" />} {live ? (site.has_changes ? "Publish Changes" : "Republish") : "Publish"}
            </button>
            <button data-testid="create-subpage-btn" onClick={() => navigate(`/national/${id}/subpages/new`)} disabled={!site.current_version_id} className={ui.btnPrimary}><Plus className="w-4 h-4" /> Create Business Subpage</button>
          </div>
        )}
      </div>

      {genRunning && <div className={`${ui.card} p-4 text-sm text-sky-300 flex items-center gap-2`} data-testid="national-generating"><Loader2 className="w-4 h-4 spin" /> Writing every page with AI — usually under a minute.</div>}
      {site.gen_status === "failed" && <div className={`${ui.card} p-4 text-sm text-rose-300`} data-testid="national-gen-error">Generation failed: {site.gen_error} <button onClick={() => call(() => api.post(`/national/${id}/regenerate`), "Retrying…")} className="underline ml-2">Retry</button></div>}
      {site.gen_status === "completed" && site.can_manage && site.subpages.length === 0 && (
        <div className={`${ui.card} p-4 flex flex-wrap items-center justify-between gap-3 border-indigo-500/40`} data-testid="subpage-cta-banner">
          <p className="text-sm text-slate-200">Your national website is ready. Add business subpages under <b>{site.custom_domain || site.intended_domain}</b>.{!site.published_version_id && <span className="text-slate-400"> Publish the national site before a subpage can go live.</span>}</p>
          <button data-testid="subpage-cta-btn" onClick={() => navigate(`/national/${id}/subpages/new`)} className={ui.btnPrimary}><Plus className="w-4 h-4" /> Create Business Subpage</button>
        </div>
      )}
      {site.publish_error && <div className={`${ui.card} p-4 text-sm text-rose-300`} data-testid="national-publish-error">{site.publish_error}{site.published_version_id ? " — the last published version is still online." : ""}</div>}

      <div className={`${ui.card} p-5 grid grid-cols-2 md:grid-cols-3 gap-5`}>
        <Info label="Website name" testid="info-name">{site.name}</Info>
        <Info label="Domain" testid="info-domain">{site.custom_domain || site.intended_domain}</Info>
        <Info label="Deployment status" testid="info-status">{deploying ? "Deploying & verifying…" : site.status}</Info>
        <Info label="Preview link" testid="info-preview">{site.current_version_id ? <a href={site.preview_url} target="_blank" rel="noreferrer" className="text-indigo-300 hover:underline">Open preview</a> : "—"}</Info>
        <Info label="Live website" testid="info-live">{site.live_url && live ? <a href={site.live_url} target="_blank" rel="noreferrer" className="text-emerald-400 hover:underline">{site.live_url}</a> : "Not published"}</Info>
        <Info label="Last updated" testid="info-updated">{fmt(site.updated_at)}</Info>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-5">
        <DomainCard site={site} reload={load} />
        {site.current_version_id && (
          <div className={`${ui.card} p-5`}>
            <h2 className={ui.cardTitle}>Pages</h2>
            <div className="flex flex-wrap gap-2 mt-3">
              {(site.pages || []).map((p) => <a key={p} href={`${site.preview_url}${p === "/" ? "" : p}`} target="_blank" rel="noreferrer" className="text-xs font-mono px-2 py-1 rounded bg-slate-800 text-slate-300 hover:text-white">{p}</a>)}
            </div>
          </div>
        )}
      </div>

      {site.current_version_id && site.gen_status !== "running" && (
        <>
          <PreviewShell url={site.preview_url} viewing={viewing} onBack={() => setViewing(null)} frameKey={frameKey} />
          {site.can_manage && <EditPanel basePath={`/national/${site.id}`} live={Boolean(site.published_version_id)} disabled={site.gen_status === "running" || site.status === "deploying"} hint="Describe a change in plain English — headlines, page text, FAQs, SEO titles, brand name, phone or logo. To add or remove services, use Edit Website." onView={setViewing} onApplied={() => { setViewing(null); setFrameKey((k) => k + 1); load(); }} />}
        </>
      )}

      <div className={`${ui.card} overflow-hidden`}>
        <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-slate-800">
          <h2 className={ui.sectionHeader} data-testid="subpages-count">Business subpages ({site.subpages.length})</h2>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative w-56"><Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input data-testid="subpage-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search businesses…" className={`${ui.input} pl-9`} /></div>
            <div className="w-40"><select data-testid="subpage-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className={ui.input}>
              <option value="">All statuses</option>
              {["draft", "generating", "deploying", "live", "failed", "unpublished"].map((s) => <option key={s} value={s}>{s}</option>)}
            </select></div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1000px]" data-testid="subpages-table">
            <thead><tr>{["Business Name", "URL", "Location", "Status", "Last Updated", "Actions"].map((h) => <th key={h} className="bg-slate-950/80 px-4 py-3 text-left text-xs font-mono tracking-wider text-slate-400 uppercase">{h}</th>)}</tr></thead>
            <tbody>
              {subs.map((s) => <SubRow key={s.id} s={s} act={act} canManage={site.can_manage} navigate={navigate} natId={id} />)}
              {!subs.length && <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-500" data-testid="subpages-empty">{site.subpages.length ? "No pages match your filters." : "No business subpages yet."}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {site.can_manage && <div className="flex justify-end"><button data-testid="national-delete-btn" onClick={deleteSite} className={ui.btnDanger}><Trash2 className="w-4 h-4" /> Delete website</button></div>}
      {confirm && <Confirm text={confirm.text} onNo={() => setConfirm(null)} onYes={() => { confirm.run(); setConfirm(null); }} />}
    </div>
  );
}
